/**
 * Turnkey error → TxError mapping.
 *
 * Relocated from frontend/src/lib/tx-engine/turnkey/errors.ts as part of the
 * auth boundary migration (spec 009 FILE 14 Group E).
 *
 * Three predicates the engine consumes:
 *   - `isTurnkeyErrorCode(err, code)`  — single-code probe
 *   - `shouldFailoverToRelay(err)`     — relay-failover trigger
 *   - `mapTurnkeyFailure(err, ...)`    — produce a `TxErrorInfo` for callers
 *
 * Platform-neutral — safe in src/auth/ core.
 */

import { createTxError, TxError, TxErrorCode, type TxErrorCodeType } from '../errors/errors.js';
import { extractMorfiErrorName } from '../errors/parse-solana-error.js';
import type {
  TGetSendTransactionStatusResponse,
  V1SolanaFailureDetails,
} from '../turnkey-types.js';
import type { TxErrorInfo, TxStage } from '../types.js';

/**
 * Scan Anchor program logs for an `AnchorError ... Error Code: <Name>`
 * line. Turnkey returns logs as plain strings — this is the only path
 * to a structured error name on the inclusion-failure branch.
 */
function extractAnchorErrorFromLogs(logs: string[]): string | undefined {
  for (const line of logs) {
    const match = /Error Code:\s*(\w+)/i.exec(line);
    if (match?.[1]) return match[1];
  }
  return undefined;
}

/**
 * Fall back to parsing `transactionErrorJson` via the existing
 * `parse-solana-error.ts::extractMorfiErrorName` helper.
 */
function extractFromTxErrorJson(
  json: string | undefined,
  programIdHint?: string,
): string | undefined {
  if (!json) return undefined;
  try {
    const parsed: unknown = JSON.parse(json);
    return programIdHint !== undefined
      ? extractMorfiErrorName(parsed, { programIdHint })
      : extractMorfiErrorName(parsed);
  } catch {
    // SILENT-CATCH: malformed JSON in Turnkey's error payload — fall through.
    return undefined;
  }
}

export interface FailedActivityContext {
  readonly currentSlippageBps?: number;
  readonly programIdHint?: string;
}

interface TurnkeyErrorLike {
  code?: string;
  message?: string;
  status?: number;
  // ALLOW-UNKNOWN: Turnkey SDK errors carry arbitrary nested payloads
  details?: unknown;
}

// ALLOW-UNKNOWN: thrown values can be any JS value; this is the boundary that narrows them
function asObject(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null) return null;
  return value as Record<string, unknown>;
}

function readString(obj: Record<string, unknown> | null, key: string): string | undefined {
  if (!obj) return undefined;
  const v = obj[key];
  return typeof v === 'string' ? v : undefined;
}

function readNumber(obj: Record<string, unknown> | null, key: string): number | undefined {
  if (!obj) return undefined;
  const v = obj[key];
  return typeof v === 'number' ? v : undefined;
}

/**
 * Probe an unknown thrown value for a Turnkey error code.
 */
// ALLOW-UNKNOWN: error-handler boundary; thrown values may be any JS value
export function isTurnkeyErrorCode(err: unknown, code: string): boolean {
  if (err === null || err === undefined) return false;

  if (err instanceof Error) {
    // ALLOW-UNKNOWN: SDK monkey-patches `code`/`status` onto Error instances at runtime
    const obj = err as unknown as TurnkeyErrorLike;
    if (obj.code === code) return true;
    if (typeof err.message === 'string' && err.message.includes(code)) return true;
  }

  const o = asObject(err);
  if (o) {
    if (readString(o, 'code') === code) return true;
    const det = asObject(o.details);
    if (det && readString(det, 'code') === code) return true;
  }
  return false;
}

/**
 * Dead-signing-session predicate. Matches the three shapes a stale Turnkey
 * client session produces:
 *   - `NO_SESSION_FOUND` — the api-key stamper has no active session
 *     (expired-and-purged, or never minted).
 *   - `SESSION_EXPIRED` — the session outlived its expiry server-side.
 *   - Turnkey NOT_FOUND "Could not find any resource to sign with" — the
 *     session is live but scoped to a DIFFERENT sub-org than the signing
 *     wallet (observed in prod: login minted a session in a new sub-org while
 *     the active wallet lives in the original one).
 *   - Turnkey UNAUTHENTICATED "could not find public key in organization" /
 *     "expired api key" — the session key no longer exists server-side. The
 *     kit maps these to `SESSION_EXPIRED` only inside its own methods; the raw
 *     http client that signing uses surfaces them as request errors. Turnkey
 *     keeps at most 10 session keys per user and deletes the oldest live one
 *     when an 11th login mints a key, so a browser whose stored session still
 *     reads as valid for days hits this on its next signature.
 * All of them are unrecoverable by retry AND by relay failover (the relay leg
 * signs client-side with the same dead session) — only a fresh login fixes
 * them, which the submit seam triggers automatically.
 */
/**
 * Safe diagnostic frame for a signing failure.
 *
 * `errorClass` alone only distinguishes `Error` from a thrown non-Error, which
 * is not enough to tell one signing failure from another once the trace is all
 * that survives. A dead session, a wallet scoped to the wrong sub-org, and key
 * material missing from the stamper store all surface here as `TX_SIGN_FAILED`;
 * `errorName` and `errorCode` are what separate them.
 *
 * Only allowlisted structured fields are kept — never the raw message, mirroring
 * how `safeErrorFrame` treats Turnkey errors on the RN provider. Both string
 * fields are charset-validated, so a field carrying prose cannot smuggle free
 * text into telemetry.
 */
// ALLOW-UNKNOWN: error-handler boundary; thrown values may be any JS value
export function describeSignFailure(err: unknown): Record<string, string | number> {
  const safe = (value: unknown): value is string =>
    typeof value === 'string' && /^[A-Za-z0-9_.-]{1,64}$/.test(value);
  let name: string | undefined;
  let code: string | undefined;
  let status: number | undefined;
  let candidate = asObject(err);
  for (let depth = 0; candidate !== null && depth < 5; depth += 1) {
    const candidateName = candidate.name;
    const candidateCode = candidate.code;
    const candidateStatus = candidate.status ?? candidate.statusCode;
    if (safe(candidateName) && (name === undefined || name === 'Error')) name = candidateName;
    if (code === undefined && safe(candidateCode)) code = candidateCode;
    if (
      code === undefined &&
      typeof candidateCode === 'number' &&
      Number.isInteger(candidateCode) &&
      candidateCode >= 0 &&
      candidateCode <= 16
    )
      code = String(candidateCode);
    if (status === undefined && typeof candidateStatus === 'number') status = candidateStatus;
    candidate = asObject(candidate.cause);
  }
  return {
    errorClass: err instanceof Error ? 'Error' : typeof err,
    ...(safe(name) ? { errorName: name } : {}),
    ...(safe(code) ? { errorCode: code } : {}),
    ...(typeof status === 'number' ? { statusCode: status } : {}),
  };
}

// ALLOW-UNKNOWN: error-handler boundary; thrown values may be any JS value
export function isTurnkeySessionError(err: unknown, depth = 0): boolean {
  if (err === null || err === undefined) return false;
  if (isTurnkeyErrorCode(err, 'NO_SESSION_FOUND')) return true;
  if (isTurnkeyErrorCode(err, 'SESSION_EXPIRED')) return true;
  const message = err instanceof Error ? err.message : (readString(asObject(err), 'message') ?? '');
  if (
    message.includes('Could not find any resource to sign with') ||
    DEAD_SESSION_KEY_MESSAGE.test(message)
  ) {
    return true;
  }
  // The kit wraps the stamper's rejection ("Failed to fetch wallets/user",
  // cause: "Key not found for publicKey: …") without repeating the cause text
  // in its own message, so the chain has to be walked. Bounded: a cyclic
  // cause must not hang an error handler.
  return depth < 5 && isTurnkeySessionError(asObject(err)?.cause, depth + 1);
}

/**
 * Turnkey's UNAUTHENTICATED rejections for a session key it no longer holds,
 * plus the kit's local stamper rejection when the browser's key store has no
 * private key for the stored session ("Key not found for publicKey: <hex>").
 * The stored session still reads as valid, so nothing else notices until a
 * signature is requested; only a fresh login re-mints the key material.
 */
const DEAD_SESSION_KEY_MESSAGE =
  /could not find public key in organization|expired api key|key not found for publickey/i;

/**
 * Failover trigger predicate. The relay is our safety net — any
 * Turnkey-side error that isn't a recoverable rate-limit should fall
 * through to the relay so the user still gets their trade through.
 */
// ALLOW-UNKNOWN: error-handler boundary; thrown values may be any JS value
export function shouldFailoverToRelay(err: unknown): boolean {
  if (err === null || err === undefined) return false;
  // Rate limit: poller handles in-place backoff. Don't failover.
  if (isTurnkeyErrorCode(err, 'RATE_LIMIT_EXCEEDED')) return false;
  // Dead/mis-scoped session: the relay leg signs with the SAME session, so
  // failover just burns a relay round-trip before failing identically.
  if (isTurnkeySessionError(err)) return false;

  if (err instanceof Error && err.message.startsWith('Turnkey error')) return true;
  if (err instanceof Error && err.name === 'TurnkeyRequestError') return true;

  const o = asObject(err);
  if (o) {
    if (readString(o, 'code') !== undefined && readString(o, 'message') !== undefined) {
      return true;
    }
    if (readNumber(o, 'code') !== undefined) return true;
  }

  // Legacy-narrow case (kept so explicit code probes still work).
  return isTurnkeyErrorCode(err, 'SIGNING_QUOTA_EXCEEDED');
}

/**
 * Heuristic: the request had `sponsor: true` but Turnkey rejected with
 * a non-quota, non-rate-limit error that looks like a configuration
 * class — treat as `TX_TURNKEY_NOT_CONFIGURED` and trigger failover.
 */
// ALLOW-UNKNOWN: error-handler boundary; thrown values may be any JS value
export function isTurnkeyNotConfigured(err: unknown): boolean {
  if (err === null || err === undefined) return false;
  if (isTurnkeyErrorCode(err, 'SIGNING_QUOTA_EXCEEDED')) return false;
  if (isTurnkeyErrorCode(err, 'RATE_LIMIT_EXCEEDED')) return false;

  // ALLOW-UNKNOWN: forward narrowing of `err` through the asObject() helper
  /* v8 ignore start -- defensive: an Error is always an object, so when asObject(err) is null err instanceof Error is false */
  const o = asObject(err) ?? (err instanceof Error ? asObject(err as unknown) : null);
  /* v8 ignore stop */
  const status = readNumber(o, 'status') ?? readNumber(o, 'httpStatus');
  const code = readString(o, 'code');

  const message = err instanceof Error ? err.message : (readString(o, 'message') ?? '');
  const sponsorshipMentioned = /sponsor/i.test(message);

  if (status === 400 || status === 403) {
    if (code === 'REQUEST_INVALID' || code === 'PERMISSION_DENIED' || sponsorshipMentioned) {
      return true;
    }
  }
  return false;
}

/**
 * Map a Turnkey error / failure to a `TxErrorInfo`. Use after the
 * engine has decided it cannot failover.
 */
export function mapTurnkeyFailure(
  // ALLOW-UNKNOWN: error-handler boundary; thrown values may be any JS value
  err: unknown,
  traceId: string,
  stage: TxStage = 'relaying',
): TxErrorInfo {
  if (err instanceof TxError) {
    return {
      code: err.code,
      stage: err.stage,
      userMessageKey: err.userMessageKey,
      traceId: err.traceId,
      recoveryAction: err.recoveryAction,
      details: err.details,
      morfiError: err.morfiError,
    };
  }

  if (isTurnkeySessionError(err)) {
    return createTxError(TxErrorCode.TX_SESSION_EXPIRED, traceId, stage);
  }
  if (isTurnkeyErrorCode(err, 'SIGNING_QUOTA_EXCEEDED')) {
    return createTxError(TxErrorCode.TX_TURNKEY_QUOTA_EXCEEDED, traceId, stage);
  }
  if (isTurnkeyErrorCode(err, 'RATE_LIMIT_EXCEEDED')) {
    return createTxError(TxErrorCode.TX_TURNKEY_RATE_LIMITED, traceId, stage);
  }
  if (isTurnkeyNotConfigured(err)) {
    return createTxError(TxErrorCode.TX_TURNKEY_NOT_CONFIGURED, traceId, stage);
  }

  // Any other rejected promise is treated as a transport-class timeout.
  const message = err instanceof Error ? err.message : String(err);
  return createTxError(TxErrorCode.TX_TURNKEY_TIMEOUT, traceId, stage, {
    message: message.slice(0, 200),
  });
}

/**
 * Map a `txStatus === 'FAILED'` activity response to a `TxErrorInfo`.
 */
export function mapFailedActivity(
  activity: TGetSendTransactionStatusResponse,
  traceId: string,
  context?: FailedActivityContext,
): TxErrorInfo {
  const solana: V1SolanaFailureDetails | undefined = activity.error?.solana;
  const source: string | undefined = solana?.source;
  const logs: string[] = solana?.logs ?? [];
  const morfiError =
    extractAnchorErrorFromLogs(logs) ??
    extractFromTxErrorJson(solana?.transactionErrorJson, context?.programIdHint);

  let recoveryActionType: 'not_retryable' | 'prompt_retry' = 'prompt_retry';
  if (source === 'preflight' || source === 'simulation') {
    recoveryActionType = 'not_retryable';
  } else if (source?.includes('inclusion')) {
    recoveryActionType = 'not_retryable';
  }

  const code: TxErrorCodeType = TxErrorCode.TX_TURNKEY_FAILED;
  // Turnkey often returns ONLY top-level `txError` / `error.message` for
  // pre-chain failures (no `error.solana`). Surface those so callers see
  // something more useful than an empty `details` object.
  const turnkeyError: string | undefined = activity.txError ?? activity.error?.message;
  const txError = createTxError(code, traceId, 'confirming', {
    ...(morfiError !== undefined ? { morfiError } : {}),
    ...(context?.currentSlippageBps !== undefined
      ? { currentSlippageBps: context.currentSlippageBps }
      : {}),
    ...(source !== undefined ? { source } : {}),
    ...(turnkeyError !== undefined ? { turnkeyError: turnkeyError.slice(0, 200) } : {}),
    ...(logs.length > 0 ? { logs: logs.slice(0, 10).map((l) => l.slice(0, 240)) } : {}),
    rpcCode: solana?.rpcCode,
    rpcMessage: solana?.rpcMessage?.slice(0, 200),
  });
  const recoveryAction: TxErrorInfo['recoveryAction'] =
    txError.recoveryAction.type === 'prompt_retry_with_higher_slippage'
      ? txError.recoveryAction
      : { type: recoveryActionType };

  return {
    code: txError.code,
    stage: txError.stage,
    userMessageKey: txError.userMessageKey,
    traceId: txError.traceId,
    recoveryAction,
    details: txError.details,
    morfiError: txError.morfiError,
  };
}
